import {useMemo, useState} from 'react'
import {useQuery} from '@tanstack/react-query'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useDialog} from '@/hooks/useDialog'
import {useServerPagination, SortDir} from '@/hooks/use-server-pagination'
import {Plus} from 'lucide-react'
import {useNavigate} from 'react-router-dom'
import {routes} from '@/lib/routes'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {FormGrid} from '@/components/shared/FormGrid'
import {MutedText} from '@/components/shared/MutedText'
import {Icon} from '@/components/shared/Icon'
import {useAuth} from '@/contexts/auth-context'
import {permissions} from '@/lib/permissions'
import {Button} from '@/components/ui/button'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {Pagination} from '@/components/ui/pagination'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {ValueListControllerValueListsEndpoint} from '@/types/endpoint-contracts'
import {CONCEPT_ICON} from '@/lib/concepts'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Textarea} from '@/components/ui/textarea'
import {ErrorBanner} from '@/components/ui/error-banner'
import type {ValueList} from '@/types/value-list'
import {PageLayout} from '@/components/shared/PageLayout'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'

export function ValueLists() {
    const services = useServices()
    const i18n = useI18n()
    usePageTitle(i18n.valueLists.pageTitle)
    const navigate = useNavigate()
    const {hasPermission} = useAuth()

    const canCreate = hasPermission(permissions.valueLists.create)

    const {
        page, pageSize, queryString,
        setPage, setPageSize, pageSizeOptions,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: ValueListControllerValueListsEndpoint,
        sortable: true,
        defaultSortBy: ValueListControllerValueListsEndpoint.sort.NAME,
        defaultSortDir: SortDir.ASC,
    })

    const createDialog = useDialog()
    const [createForm, setCreateForm] = useState({name: '', displayName: '', description: ''})

    const {data: listsPage, isLoading, error} = useQuery({
        queryKey: ['value-lists', queryString, page, pageSize],
        queryFn: () => services.valueLists.list(
            Object.fromEntries(new URLSearchParams(queryString))),
    })

    const lists = listsPage?.content ?? []
    const totalItems = listsPage?.totalElements ?? 0
    const totalPages = listsPage?.totalPages ?? 0

    const hasActiveFilters = useMemo(() => {
        return !!searchAndFilters.q
            || Object.keys(searchAndFilters.filters).length > 0
            || Object.keys(searchAndFilters.dynamicAttributes).length > 0
    }, [searchAndFilters])

    const createMutation = useEntityMutation({
        mutationFn: (data: {name: string; displayName: string; description: string | null}) =>
            services.valueLists.create(data),
        invalidate: ['value-lists'],
        successMessage: i18n.valueLists.messages.createSuccess,
        closeDialog: createDialog.close,
        onSuccess: (created: ValueList) => {
            setCreateForm({name: '', displayName: '', description: ''})
            navigate(routes.schema.valueLists.detail(created.id))
        },
    })

    function handleCreate() {
        createMutation.mutate({
            name: createForm.name,
            displayName: createForm.displayName,
            description: createForm.description || null,
        })
    }

    return (
        <>
            <ErrorBanner error={error}/>
            <PageLayout
                variant="list"
                icon={CONCEPT_ICON.valueList}
                title={i18n.valueLists.title}
                count={totalItems}
                description={i18n.valueLists.subtitle}
                data-testid="value-lists"
                actions={canCreate ? (
                    <Button
                        data-testid="btn-create-value-list"
                        onClick={createDialog.open}
                    >
                        <Icon icon={Plus} className="mr-2"/>
                        {i18n.valueLists.actions.createList}
                    </Button>
                ) : undefined}
                filters={
                    <UnifiedSearchBar
                        contract={ValueListControllerValueListsEndpoint}
                        value={searchAndFilters}
                        onChange={setSearchAndFilters}
                    />
                }
                pagination={totalItems > 0 ? (
                    <Pagination
                        page={page}
                        pageSize={pageSize}
                        totalItems={totalItems}
                        totalPages={totalPages}
                        onPageChange={setPage}
                        onPageSizeChange={setPageSize}
                        pageSizeOptions={pageSizeOptions}
                    />
                ) : undefined}
            >
                {isLoading ? (
                    <MutedText block centered className="py-8">{i18n.common.loading}</MutedText>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead data-testid="col-name">{i18n.valueLists.columns.name}</TableHead>
                                <TableHead data-testid="col-display-name">{i18n.valueLists.columns.displayName}</TableHead>
                                <TableHead data-testid="col-description">{i18n.valueLists.columns.description}</TableHead>
                                <TableHead data-testid="col-active">{i18n.valueLists.columns.active}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {lists.map((list: ValueList) => (
                                <ClickableTableRow
                                    key={list.id}
                                    data-testid={`value-list-row-${list.id}`}
                                    to={routes.schema.valueLists.detail(list.id)}
                                >
                                    <TableCell className="font-mono text-sm">{list.name}</TableCell>
                                    <TableCell>{list.displayName}</TableCell>
                                    <TableCell className="text-muted-foreground">{list.description ?? '—'}</TableCell>
                                    <TableCell>
                                        <Badge variant={list.active ? BadgeVariant.SUCCESS : BadgeVariant.WARNING}>
                                            {list.active ? i18n.common.active : i18n.common.inactive}
                                        </Badge>
                                    </TableCell>
                                </ClickableTableRow>
                            ))}
                            {lists.length === 0 && (
                                <TableEmptyState
                                    colSpan={4}
                                    hasFilters={hasActiveFilters}
                                    matchMessage={i18n.valueLists.noLists}
                                    emptyMessage={i18n.valueLists.noLists}
                                />
                            )}
                        </TableBody>
                    </Table>
                )}
            </PageLayout>

            <Dialog open={createDialog.isOpen} onOpenChange={createDialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.valueLists.form.createTitle}</DialogTitle>
                        <DialogDescription>{i18n.valueLists.form.createDescription}</DialogDescription>
                    </DialogHeader>
                    <FormGrid>
                        <FormGrid>
                            <Label htmlFor="vl-name">{i18n.valueLists.columns.name}</Label>
                            <Input
                                id="vl-name"
                                data-testid="input-vl-name"
                                value={createForm.name}
                                onChange={e => setCreateForm(f => ({...f, name: e.target.value}))}
                                placeholder={i18n.valueLists.form.namePlaceholder}
                            />
                            <MutedText size="xs">{i18n.valueLists.form.nameHelp}</MutedText>
                        </FormGrid>
                        <FormGrid>
                            <Label htmlFor="vl-display-name">{i18n.valueLists.columns.displayName}</Label>
                            <Input
                                id="vl-display-name"
                                data-testid="input-vl-display-name"
                                value={createForm.displayName}
                                onChange={e => setCreateForm(f => ({...f, displayName: e.target.value}))}
                                placeholder={i18n.valueLists.form.displayNamePlaceholder}
                            />
                        </FormGrid>
                        <FormGrid>
                            <Label htmlFor="vl-description">{i18n.valueLists.columns.description}</Label>
                            <Textarea
                                id="vl-description"
                                data-testid="input-vl-description"
                                value={createForm.description}
                                onChange={e => setCreateForm(f => ({...f, description: e.target.value}))}
                                placeholder={i18n.valueLists.form.descriptionPlaceholder}
                                rows={3}
                            />
                        </FormGrid>
                    </FormGrid>
                    <SaveCancelFooter
                        testIdPrefix="create-vl"
                        onCancel={createDialog.close}
                        saving={createMutation.isPending}
                        disabled={!createForm.name || !createForm.displayName}
                        saveLabel={i18n.common.create}
                        savingLabel={i18n.common.creating}
                        saveType="button"
                        onSave={handleCreate}
                    />
                </DialogContent>
            </Dialog>
        </>
    )
}
